import AppConfirmDialog from '../../../../components/ui/AppConfirmDialog';
import AppFormSelect from '../../../../components/ui/AppFormSelect';
import AppFormInput from '../../../../components/ui/AppFormInput';
import React, {useEffect, useRef, useState} from 'react';
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {InputSwitch} from 'primereact/inputswitch';
import {Toast} from 'primereact/toast';
import {Tag} from 'primereact/tag';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {ProgressSpinner} from 'primereact/progressspinner';

import iVeriService from '../../../../services/i-veri-service';
import apiClient from '../../../../services/api-client';
import StorageHelper from "../../../../utils/storage-helper";
import DeleteRecurringPaymentDialog from './DeleteRecurringPaymentDialog';

const RecurringPaymentsDialog = ({ visible, onHide, policyNumber }) => {
    const toast = useRef(null);

    // Setup Form State
    const [holderName, setHolderName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [expiryMonth, setExpiryMonth] = useState('');
    const [expiryYear, setExpiryYear] = useState('');
    const [cvv, setCvv] = useState('');
    
    // Policy Selection State
    const [userPolicies, setUserPolicies] = useState([]);
    const [selectedPolicy, setSelectedPolicy] = useState(policyNumber || '');
    const [isFetchingPolicies, setIsFetchingPolicies] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        action: null,
        rowData: null,
        title: '',
        message: '',
        confirmLabel: '',
        confirmStyle: ''
    });

    const openConfirmDialog = (action, rowData) => {
        if (action === 'TOGGLE') {
            setConfirmConfig({
                action,
                rowData,
                title: rowData.isActive ? 'Pause Recurring Payment?' : 'Resume Recurring Payment?',
                message: `Are you sure you want to ${rowData.isActive ? 'pause' : 'resume'} this recurring payment?`,
                confirmLabel: rowData.isActive ? 'Pause' : 'Resume',
                confirmStyle: rowData.isActive ? 'btn-warning text-white' : 'btn-success text-white'
            });
        } else if (action === 'RETRY') {
            setConfirmConfig({
                action,
                rowData,
                title: 'Retry Failed Payment?',
                message: 'Are you sure you want to retry this failed payment?',
                confirmLabel: 'Retry',
                confirmStyle: 'btn-error text-white'
            });
        }
        document.getElementById('recurring_confirm_modal').showModal();
    };

    const handleConfirmAction = async () => {
        if (!confirmConfig.rowData) return;
        if (confirmConfig.action === 'TOGGLE') {
            await handleToggleStatus(confirmConfig.rowData.id, confirmConfig.rowData.policyNumber, confirmConfig.rowData.isActive);
        } else if (confirmConfig.action === 'RETRY') {
            await handleRetry(confirmConfig.rowData.id, confirmConfig.rowData.policyNumber);
        }
    };

    // Multi-step delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

    const openDeleteDialog = (rowData) => {
        setDeleteTarget(rowData);
        setDeleteDialogVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        await handleDelete(deleteTarget.id, deleteTarget.policyNumber);
        setDeleteDialogVisible(false);
        setDeleteTarget(null);
    };


    // Loading States
    const [isSetupLoading, setIsSetupLoading] = useState(false);
    const [isFetchingLoading, setIsFetchingLoading] = useState(false);
    const [isToggleLoading, setIsToggleLoading] = useState({});
    const [isRetryLoading, setIsRetryLoading] = useState({});
    const [isDeleteLoading, setIsDeleteLoading] = useState({});
    
    // User Settings State
    const [recurringPayments, setRecurringPayments] = useState([]);

    const fetchRecurringPayments = async () => {
        setIsFetchingLoading(true);
        try {
            const response = await iVeriService.getUserRecurringPayments();
            const data = response.data?.responseBody || [];
            const mappedData = data.map(item => ({
                id: item.id,
                policyNumber: item.policyNumber,
                maskedPan: item.maskedPan,
                cardExpiry: item.cardExpiry,
                contactPhone: item.contactPhone,
                contactEmail: item.contactEmail,
                nextPaymentDate: item.nextPaymentDate,
                attempts: item.attempts,
                isActive: item.status === 'ACTIVE',
                status: item.status,
                amount: item.amount || 0 // Included just in case the API returns it
            }));
            setRecurringPayments(mappedData);
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to fetch recurring payments';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: errMsg, life: 3000 });
            setRecurringPayments([]);
        } finally {
            setIsFetchingLoading(false);
        }
    };

    const fetchUserPolicies = async () => {
        setIsFetchingPolicies(true);
        try {
            const userProfile = StorageHelper.get('userProfile');
            if (userProfile && userProfile.idnumber) {
                const response = await apiClient.get('policy/' + userProfile.idnumber);
                if (response.data?.status === "SUCCESS" && response.data?.responseBody) {
                    const fetchedData = response.data.responseBody;
                    let policyList = [];
                    if (Array.isArray(fetchedData)) {
                        policyList = fetchedData.map(p => {
                            const pNumber = p.Policy_Number || p.policyNumber || p.MainMember?.Policy_Number;
                            return { label: pNumber, value: pNumber };
                        });
                    } else {
                        const pNumber = fetchedData.Policy_Number || fetchedData.policyNumber || fetchedData.MainMember?.Policy_Number;
                        policyList = [{ label: pNumber, value: pNumber }];
                    }
                    policyList = policyList.filter(p => p.value);
                    setUserPolicies(policyList);
                    
                    if (!selectedPolicy && policyList.length > 0) {
                        setSelectedPolicy(policyList[0].value);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to fetch user policies:", error);
        } finally {
            setIsFetchingPolicies(false);
        }
    };

    // Clear form and fetch data on open
    useEffect(() => {
        if (visible) {
            setHolderName('');
            setCardNumber('');
            setExpiryMonth('');
            setExpiryYear('');
            setCvv('');
            if (policyNumber) setSelectedPolicy(policyNumber);
            fetchRecurringPayments();
            fetchUserPolicies();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, policyNumber]);

    const validateSetupForm = () => {
        const cardNumberValid = /^\d{13,19}$/.test(cardNumber.replace(/\s+/g, ''));
        const expiryMonthValid = /^(0[1-9]|1[0-2])$/.test(expiryMonth);
        
        let expiryYearValid = /^\d{4}$/.test(expiryYear);
        let futureDateValid = true;
        if (expiryMonthValid && expiryYearValid) {
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            const expYearNum = parseInt(expiryYear, 10);
            const expMonthNum = parseInt(expiryMonth, 10);
            
            if (expYearNum < currentYear || (expYearNum === currentYear && expMonthNum < currentMonth)) {
                futureDateValid = false;
            }
        }

        const cvvValid = /^\d{3,4}$/.test(cvv);
        const holderNameValid = holderName.trim().length > 0;
        const selectedPolicyValid = selectedPolicy && selectedPolicy.trim().length > 0;

        if (!selectedPolicyValid) return 'Please select a policy.';
        if (!cardNumberValid) return 'Invalid card number.';
        if (!expiryMonthValid || !expiryYearValid || !futureDateValid) return 'Invalid or past expiry date.';
        if (!cvvValid) return 'Invalid CVV.';
        if (!holderNameValid) return 'Holder name is required.';

        return null;
    };

    const handleSetupSubmit = async (e) => {
        e.preventDefault();
        const error = validateSetupForm();
        if (error) {
            toast.current.show({ severity: 'warn', summary: 'Validation Error', detail: error, life: 5000 });
            return;
        }

        setIsSetupLoading(true);
        try {
            const payload = {
                policyNumber: selectedPolicy,
                number: cardNumber.replace(/\s+/g, ''),
                expiryMonth,
                expiryYear,
                cvv,
                holderName: holderName.trim()
            };
            
            await iVeriService.setupRecurringPayment(payload);
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Recurring payment setup successfully', life: 3000 });

            setHolderName('');
            setCardNumber('');
            setExpiryMonth('');
            setExpiryYear('');
            setCvv('');

            await fetchRecurringPayments();
            
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to setup recurring payment';
            toast.current.show({ severity: 'error', summary: 'Setup Failed', detail: errMsg, life: 5000 });
        } finally {
            setIsSetupLoading(false);
        }
    };

    const handleToggleStatus = async (paymentId, policyNumber, currentStatus) => {
        setIsToggleLoading(prev => ({ ...prev, [paymentId]: true }));
        const newStatus = !currentStatus;
        try {
            await iVeriService.toggleStatus(policyNumber, newStatus);
            toast.current.show({ severity: 'success', summary: 'Status Updated', detail: `Payment status ${newStatus ? 'resumed' : 'paused'}.`, life: 3000 });
            
            // Update local state to reflect change
            setRecurringPayments(prev => prev.map(p => p.id === paymentId ? { ...p, isActive: newStatus, status: newStatus ? 'ACTIVE' : 'PAUSED' } : p));
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to update status';
            toast.current.show({ severity: 'error', summary: 'Error', detail: errMsg, life: 5000 });
        } finally {
            setIsToggleLoading(prev => ({ ...prev, [paymentId]: false }));
        }
    };

    const handleRetry = async (paymentId, policyNumber) => {
        setIsRetryLoading(prev => ({ ...prev, [paymentId]: true }));
        try {
            await iVeriService.retryFailedRecurringPayment(policyNumber);
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Payment retried successfully', life: 3000 });
            
            // Update local state to reflect change (e.g., status back to active)
            setRecurringPayments(prev => prev.map(p => p.id === paymentId ? { ...p, isActive: true, status: 'ACTIVE' } : p));
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to retry payment';
            toast.current.show({ severity: 'error', summary: 'Retry Failed', detail: errMsg, life: 5000 });
        } finally {
            setIsRetryLoading(prev => ({ ...prev, [paymentId]: false }));
        }
    };

    const handleDelete = async (paymentId, policyNumber) => {
        setIsDeleteLoading(prev => ({ ...prev, [paymentId]: true }));
        try {
            await iVeriService.deleteRecurringPayment(policyNumber);
            toast.current.show({ severity: 'success', summary: 'Deleted', detail: 'Recurring payment setup removed.', life: 3000 });

            // Remove the row locally; if it was the last one, the dialog falls back to the setup form
            setRecurringPayments(prev => prev.filter(p => p.id !== paymentId));
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to delete recurring payment';
            toast.current.show({ severity: 'error', summary: 'Delete Failed', detail: errMsg, life: 5000 });
        } finally {
            setIsDeleteLoading(prev => ({ ...prev, [paymentId]: false }));
        }
    };

    const actionBodyTemplate = (rowData) => {
        const rowBusy = isToggleLoading[rowData.id] || isRetryLoading[rowData.id] || isDeleteLoading[rowData.id];
        return (
            <div className="flex gap-2 align-items-center">
                <InputSwitch
                    checked={rowData.isActive}
                    onChange={() => openConfirmDialog('TOGGLE', rowData)}
                    disabled={rowBusy}
                />

                {rowData.status === 'FAILED' && (
                    <Button
                        icon="pi pi-refresh"
                        rounded text severity="danger" aria-label="Retry"
                        onClick={() => openConfirmDialog('RETRY', rowData)}
                        loading={isRetryLoading[rowData.id]}
                        disabled={isToggleLoading[rowData.id] || isDeleteLoading[rowData.id]}
                        tooltip="Retry Failed Payment"
                    />
                )}

                <Button
                    icon="pi pi-trash"
                    rounded text severity="danger" aria-label="Delete"
                    onClick={() => openDeleteDialog(rowData)}
                    loading={isDeleteLoading[rowData.id]}
                    disabled={isToggleLoading[rowData.id] || isRetryLoading[rowData.id]}
                    tooltip="Delete Recurring Payment"
                />
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        let severity = 'success';
        if (rowData.status === 'FAILED') severity = 'danger';
        else if (rowData.status === 'PAUSED' || !rowData.isActive) severity = 'warning';
        
        return <Tag value={rowData.status} severity={severity}></Tag>;
    };

    return (
        <Dialog 
            header="Manage Recurring Payments" 
            visible={visible} 
            style={{ width: '50vw' }} 
            onHide={onHide}
            maximizable
        >
            <AppConfirmDialog
                id="recurring_confirm_modal"
                title={confirmConfig.title}
                message={confirmConfig.message}
                onConfirm={handleConfirmAction}
                confirmLabel={confirmConfig.confirmLabel}
                confirmStyle={confirmConfig.confirmStyle}
                icon="pi pi-exclamation-circle"
            />
            <DeleteRecurringPaymentDialog
                visible={deleteDialogVisible}
                onHide={() => { setDeleteDialogVisible(false); setDeleteTarget(null); }}
                rowData={deleteTarget}
                onConfirmDelete={handleConfirmDelete}
                loading={deleteTarget ? !!isDeleteLoading[deleteTarget.id] : false}
            />
            <Toast ref={toast} />
            
            {isFetchingLoading ? (
                <div className="flex justify-content-center align-items-center p-5">
                    <ProgressSpinner style={{width: '50px', height: '50px'}} strokeWidth="4" />
                </div>
            ) : recurringPayments.length > 0 ? (
                <div>
                    <h5 className="mb-3">Your Recurring Payments</h5>
                    <DataTable value={recurringPayments} emptyMessage="No recurring payments found." responsiveLayout="scroll">
                        <Column field="policyNumber" header="Policy Number"></Column>
                        <Column field="maskedPan" header="Card"></Column>
                        <Column field="nextPaymentDate" header="Next Payment Date"></Column>
                        <Column field="status" header="Status" body={statusBodyTemplate}></Column>
                        <Column header="Actions" body={actionBodyTemplate} style={{ width: '150px' }}></Column>
                    </DataTable>
                    <div className="mt-4 flex justify-content-end">
                        <Button label="Add Another" icon="pi pi-plus" className="p-button-text" onClick={() => setRecurringPayments([])} />
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSetupSubmit} className="p-fluid">
                    <h5 className="mb-3">Setup New Recurring Payment</h5>
                    <AppFormSelect
                            label="Policy Number"
                            id="policyNumberSelect" 
                            value={selectedPolicy} 
                            options={userPolicies} 
                            onChange={(e) => setSelectedPolicy(e.target.value)} 
                            placeholder="Select a Policy"
                            disabled={isSetupLoading || isFetchingPolicies}
                            className="w-full"
                            required
                        />
                    <AppFormInput
                            label="Holder Name"
                            id="holderName" 
                            value={holderName} 
                            onChange={(e) => setHolderName(e.target.value)} 
                            placeholder="John Doe"
                            disabled={isSetupLoading}
                            required
                        />
                    <AppFormInput
                            label="Card Number"
                            id="cardNumber" 
                            value={cardNumber} 
                            onChange={(e) => setCardNumber(e.target.value.replace(/[^\d]/g, ''))} 
                            placeholder="1234 5678 1234 5678"
                            disabled={isSetupLoading}
                            maxLength={19}
                            required
                        />
                    <div className="formgrid grid mb-3 flex gap-3">
                        <div className="col flex-1"><AppFormInput
                            label="Expiry Month"
                            id="expiryMonth"
                                value={expiryMonth}
                                onChange={(e) => setExpiryMonth(e.target.value.replace(/[^\d]/g, ''))}
                                placeholder="MM"
                                maxLength={2}
                                disabled={isSetupLoading}
                                required
                        /></div>
                        <div className="col flex-1"><AppFormInput
                            label="Expiry Year"
                            id="expiryYear"
                                value={expiryYear}
                                onChange={(e) => setExpiryYear(e.target.value.replace(/[^\d]/g, ''))}
                                placeholder="YYYY"
                                maxLength={4}
                                disabled={isSetupLoading}
                                required
                        /></div>
                        <div className="col flex-1"><AppFormInput
                            label="CVV"
                            id="cvv"
                                value={cvv}
                                onChange={(e) => setCvv(e.target.value.replace(/[^\d]/g, ''))}
                                placeholder="123"
                                maxLength={4}
                                disabled={isSetupLoading}
                                required
                        /></div>
                    </div>
                    <div className="flex justify-content-end mt-4">
                        <Button 
                            type="submit" 
                            label="Setup Recurring Payment" 
                            icon="pi pi-check" 
                            loading={isSetupLoading} 
                            disabled={isSetupLoading}
                        />
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default RecurringPaymentsDialog;
